import Http from '@/services/http';
import {
  ChartQuery,
  PhysicalChemicalEvolutionChart,
  PagedRequest,
  PhysicalChemicalSamplePagedResponse,
  LotAndParameterTableResponse,
  Lookup,
  ReprocessSampleRequest,
  SampleLookup,
} from 'sigma-mappings';
import { formatter as dateTimeFormatter } from '@/services/dateTime/datetimeService';
import { formats, timezones } from '@/constants/localizer';
import { fieldsDefinitions as sampleFieldsDefinitions } from '@/domain/data/installation/engineering/physicalChemical/sample';
import translate from '@/services/translate';
import msis127Error from '@/domain/data/installation/engineering/cromaMSIS127ErrorHandler';

const urls = {
  samplesList: assetModuleId => `/engenharia/${assetModuleId}/fisico-quimico/amostras/listagem`,
  getChartData: assetModuleId => `/engenharia/${assetModuleId}/fisico-quimico/amostras/evolucao`,
  getLotAndParametersTables: (assetModuleId, id) => `/engenharia/${assetModuleId}/fisico-quimico/amostras/${id}`,
  getSamplesLookup: assetModuleId => `/engenharia/${assetModuleId}/fisico-quimico/amostras/lotes/lookup`,
  reprocessSamples: assetModuleId => `/engenharia/${assetModuleId}/fisico-quimico/amostras/reprocessar`,
  deleteSample: assetModuleId => `/engenharia/${assetModuleId}/fisico-quimico/amostras`,
  samplesIdLookup: assetModuleId => `/engenharia/${assetModuleId}/fisico-quimico/amostras/lookup`,
};

const sampleTypeLabel = value => `enums.sample-type.${value}`;

const samplingMomentLabel = value => `enums.sampling-moment.${value}`;

const sedimentTranslate = value => (value
  ? 'enums.sediment.with-sediments'
  : 'enums.sediment.without-sediments');

const corrosiveSulfurTranslate = value => (value
  ? 'enums.sulfur.corrosive'
  : 'enums.sulfur.non-corrosive');

const genericDateFieldFormatter = value => dateTimeFormatter(
  value,
  formats.DATE_SHORT,
  timezones.UTC,
);

const sampleListDataFormatters = {
  date: genericDateFieldFormatter,
  analyzeDate: genericDateFieldFormatter,
};

const sampleListValueTranslationPathResolvers = {
  sediments: sedimentTranslate,
  corrosiveSulfur: corrosiveSulfurTranslate,
  sampleType: sampleTypeLabel,
  samplingMoment: samplingMomentLabel,
};

const error = {
  ...msis127Error,
};

function mapResponseWithName(data) {
  return data.map((item) => {
    const fieldDefinition = sampleFieldsDefinitions[item.nameField];
    const name = fieldDefinition
      ? translate(fieldDefinition.name, {
          temperature: fieldDefinition.temperature,
        })
      : item.nameField;
    return {
      ...item,
      values: item.values.filter(value => value.y !== undefined),
      name,
    };
  });
}

export default {
  getSampleList(assetModuleId, filter) {
    const http = new Http(error);
    const mappedFilter = new PagedRequest(filter);
    return http.post(urls.samplesList(assetModuleId), mappedFilter)
      .then(({ data }) => new PhysicalChemicalSamplePagedResponse(data))
      .catch(error.msis127ErrorHandler);
  },
  getSamplesIdLookup(assetModuleId, sampleIds) {
    const http = new Http(error);
    return http.post(urls.samplesIdLookup(assetModuleId), { ids: sampleIds })
      .then(({ data }) => data.map(item => new SampleLookup(item)))
      .catch(error.msis127ErrorHandler);
  },
  deleteSample(assetModuleId, ids) {
    const { msis127ErrorHandler, errorHandling } = msis127Error;
    const http = new Http({
      errorHandling,
    });
    return http
      .delete(urls.deleteSample(assetModuleId), { data: { ids } })
      .catch(msis127ErrorHandler);
  },

  /**
   * Obtém uma lista de amostras de dados históricos.
   * @param {GUID} assetModuleId
   */
  getChartData(assetModuleId, chartFilters) {
    const http = new Http();
    const chartQuery = new ChartQuery(chartFilters);
    return http.get(urls.getChartData(assetModuleId), { params: chartQuery })
      .then(({ data }) => {
        const mappedResponse = data.map(item => new PhysicalChemicalEvolutionChart(item));
        return mapResponseWithName(mappedResponse);
      });
  },
  getSampleTables(assetModuleId, sampleId) {
  /**
   * Obtém uma lista de amostras de dados históricos.
   * @param {GUID} assetModuleId
   * @param {GUID} sampleId
   */
    const http = new Http();
    return http.get(urls.getLotAndParametersTables(assetModuleId, sampleId))
      .then(({ data }) => new LotAndParameterTableResponse(data));
  },
  /**
   * Obtém uma lista de amostras de dados históricos.
   * @param {GUID} assetModuleId
   */
  getReportLookup(assetModuleId) {
    const http = new Http(error);
    return http.get(urls.getSamplesLookup(assetModuleId))
      .then(({ data }) => data.map(item => new Lookup(item)))
      .catch(error.msis127ErrorHandler);
  },
  /**
   * Solicita o reprocessamento das amostras pendentes para o ativo.
   * Esta funcionalidade é utilizada na aba geral de físico-químico.
   */
  reprocessPendingSamples(assetModuleId, payload) {
    const mappedPayload = new ReprocessSampleRequest(payload);
    const http = new Http();
    return http
      .post(urls.reprocessSamples(assetModuleId), mappedPayload)
      .then(({ data }) => data);
  },
  /**
   * Salva na store do navegador a configuração que fez
   *  nas colunas da tabela principal da aba geral.
   * @param {Object} payload
   * Essa funcionalidade só estará presente na entrega 2
   */
  localSaveColumnsSettings(payload) {
    return localStorage.setItem('physical-chemical-general-tab-columns', JSON.stringify(payload));
  },
  localGetColumnsSettings() {
    return JSON.parse(localStorage.getItem('physical-chemical-general-tab-columns'));
  },
  localRemoveColumnsSettings() {
    return localStorage.removeItem('physical-chemical-general-tab-columns');
  },
  sampleListRenderingHelpers: {
    dataFormatters: sampleListDataFormatters,
    valueTranslationPathResolvers: sampleListValueTranslationPathResolvers,
  },
};
